/// <reference types="google.maps" />

import { importLibrary } from '@googlemaps/js-api-loader';
import {
  type MaybeRefOrGetter,
  onMounted,
  onScopeDispose,
  ref,
  type ShallowRef,
  toValue,
  watch,
} from 'vue';
import type { GeoPosition } from '../api/geo.types.ts';
import { useGeoConfig } from './geo.config.ts';
import {
  GoogleMapsAuthError,
  onGoogleMapsAuthFailure,
} from './google-maps-auth.ts';

export const ITALY_CENTER: GeoPosition = { lat: 42.5, lng: 12.5 };
export const ITALY_ZOOM = 5;
export const POSITION_ZOOM = 17;

export const LocationMapStatus = {
  LOADING: 'LOADING',
  READY: 'READY',
  ERROR: 'ERROR',
} as const;

export type LocationMapStatus =
  (typeof LocationMapStatus)[keyof typeof LocationMapStatus];

type MarkerPosition = google.maps.marker.AdvancedMarkerElement['position'];

export type UseLocationMapOptions = {
  container: Readonly<ShallowRef<HTMLElement | null>>;
  position: MaybeRefOrGetter<GeoPosition | null>;
  markerTitle: MaybeRefOrGetter<string>;
  onError?: (reason: unknown) => void;
};

const toGeoPosition = (value: MarkerPosition): GeoPosition | null => {
  if (!value) return null;
  if (typeof value.lat === 'function' && typeof value.lng === 'function') {
    return { lat: value.lat(), lng: value.lng() };
  }
  return { lat: Number(value.lat), lng: Number(value.lng) };
};

const isSamePosition = (a: GeoPosition | null, b: GeoPosition | null) =>
  a?.lat === b?.lat && a?.lng === b?.lng;

export const useLocationMap = ({
  container,
  position,
  markerTitle,
  onError,
}: UseLocationMapOptions) => {
  const config = useGeoConfig();
  const status = ref<LocationMapStatus>(LocationMapStatus.LOADING);

  let map: google.maps.Map | null = null;
  let marker: google.maps.marker.AdvancedMarkerElement | null = null;
  let disposed = false;

  const fail = (reason: unknown) => {
    if (status.value === LocationMapStatus.ERROR) return;
    map = null;
    marker = null;
    status.value = LocationMapStatus.ERROR;
    onError?.(reason);
  };

  const stopAuthFailure = onGoogleMapsAuthFailure(() => {
    if (!disposed) fail(new GoogleMapsAuthError());
  });

  const showPosition = (next: GeoPosition | null) => {
    if (!map || !marker) return;
    try {
      if (isSamePosition(toGeoPosition(marker.position), next)) return;
      marker.position = next;
      if (next) {
        map.panTo(next);
        map.setZoom(POSITION_ZOOM);
      } else {
        map.setCenter(ITALY_CENTER);
        map.setZoom(ITALY_ZOOM);
      }
    } catch (reason) {
      fail(reason);
    }
  };

  const init = async () => {
    const element = container.value;
    if (!element || !config?.googleMapsApiKey || !config.googleMapId) return;
    try {
      const [{ Map: GoogleMap }, { AdvancedMarkerElement }] = await Promise.all(
        [importLibrary('maps'), importLibrary('marker')],
      );
      if (disposed || status.value === LocationMapStatus.ERROR) return;

      const initial = toValue(position);
      map = new GoogleMap(element, {
        mapId: config.googleMapId,
        center: initial ?? ITALY_CENTER,
        zoom: initial ? POSITION_ZOOM : ITALY_ZOOM,
        disableDefaultUI: true,
        zoomControl: true,
        clickableIcons: false,
        gestureHandling: 'cooperative',
      });
      marker = new AdvancedMarkerElement({
        map,
        position: initial,
        title: toValue(markerTitle),
      });
      status.value = LocationMapStatus.READY;
    } catch (reason) {
      if (!disposed) fail(reason);
    }
  };

  watch(() => toValue(position), showPosition);

  onMounted(init);

  onScopeDispose(() => {
    disposed = true;
    stopAuthFailure();
    if (marker) marker.map = null;
    marker = null;
    map = null;
  });

  return { status };
};
